/**
 * Notification Settings types for API contracts between frontend and backend
 * Used for user notification preferences (per-setting granular control)
 */
import { z } from "zod";
import { uuidSchema } from "./validation-common.js";
// ============================================
// INPUT SCHEMAS
// ============================================
/**
 * Query schema for GET /api/user-profile/notification-settings
 * companyId is required for authorization context
 */
export const GetNotificationSettingsQuerySchema = z.object({
    companyId: uuidSchema,
});
/**
 * Input schema for PATCH /api/user-profile/notification-settings/master
 * Toggle all notifications on/off
 */
export const UpdateMasterToggleInputSchema = z.object({
    enabled: z.boolean(),
});
/**
 * Path parameters for PATCH /api/user-profile/notification-settings/:categoryId/:settingId
 */
export const UpdateSettingParamsSchema = z.object({
    categoryId: z.string().min(1).max(100),
    settingId: z.string().min(1).max(100),
});
/**
 * Input schema for PATCH /api/user-profile/notification-settings/:categoryId/:settingId
 * Update specific setting channels
 */
export const UpdateSettingInputSchema = z.object({
    pushEnabled: z.boolean().optional(),
    emailEnabled: z.boolean().optional(),
    smsEnabled: z.boolean().optional(),
});
// ============================================
// RESPONSE SCHEMAS
// ============================================
/**
 * Individual notification setting response
 */
export const NotificationSettingResponseSchema = z.object({
    id: z.uuid(),
    categoryId: z.string(),
    settingId: z.string(),
    enabled: z.boolean(),
    pushEnabled: z.boolean(),
    emailEnabled: z.boolean(),
    smsEnabled: z.boolean(),
});
/**
 * Response for GET /api/user-profile/notification-settings
 * Returns all notification settings for the user
 */
export const NotificationSettingsResponseSchema = z.object({
    settings: z.array(NotificationSettingResponseSchema),
});
/**
 * Response for PATCH /api/user-profile/notification-settings/master
 */
export const MasterToggleResponseSchema = z.object({
    enabled: z.boolean(),
    updatedCount: z.number(),
});
// ============================================
// DEFAULT NOTIFICATION SETTINGS
// ============================================
/**
 * Default notification settings that every user should have
 * These are created lazily when the user first accesses their settings
 */
export const DEFAULT_NOTIFICATION_SETTINGS = [
    // Schedule category
    {
        categoryId: "schedule",
        settingId: "shift_published",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    {
        categoryId: "schedule",
        settingId: "shift_updated",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    {
        categoryId: "schedule",
        settingId: "shift_reminder",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    {
        categoryId: "schedule",
        settingId: "shift_cancelled",
        enabled: true,
        pushEnabled: true,
        emailEnabled: true,
        smsEnabled: false,
    },
    // Time off category
    {
        categoryId: "timeoff",
        settingId: "request_approved",
        enabled: true,
        pushEnabled: true,
        emailEnabled: true,
        smsEnabled: false,
    },
    {
        categoryId: "timeoff",
        settingId: "request_rejected",
        enabled: true,
        pushEnabled: true,
        emailEnabled: true,
        smsEnabled: false,
    },
    {
        categoryId: "timeoff",
        settingId: "new_request",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    // Chat category
    {
        categoryId: "chat",
        settingId: "new_message",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    {
        categoryId: "chat",
        settingId: "mention",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    // Approvals category
    {
        categoryId: "approvals",
        settingId: "pending_approval",
        enabled: true,
        pushEnabled: true,
        emailEnabled: true,
        smsEnabled: false,
    },
    {
        categoryId: "approvals",
        settingId: "approval_resolved",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    {
        categoryId: "approvals",
        settingId: "swap_accepted",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    {
        categoryId: "approvals",
        settingId: "swap_approved",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    {
        categoryId: "approvals",
        settingId: "swap_rejected",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    {
        categoryId: "approvals",
        settingId: "timecard_approved",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    {
        categoryId: "approvals",
        settingId: "timecard_returned",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    // Schedule category (open shift notifications)
    {
        categoryId: "schedule",
        settingId: "open_shift_approved",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    {
        categoryId: "schedule",
        settingId: "open_shift_rejected",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    {
        categoryId: "schedule",
        settingId: "open_shift_filled",
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        smsEnabled: false,
    },
    // System category
    {
        categoryId: "system",
        settingId: "alerts",
        enabled: true,
        pushEnabled: true,
        emailEnabled: true,
        smsEnabled: false,
    },
    {
        categoryId: "system",
        settingId: "welcome",
        enabled: true,
        pushEnabled: false,
        emailEnabled: true,
        smsEnabled: false,
    },
];
